import { ComponentType, lazy } from 'react';
import { CompanyCheck } from 'src/easyclaim.app.components/common/CompanyCheck';
import { CompleteModalItem } from 'src/easyclaim.app.components/modals/CompleteModal';
import { isReviewGroupedCompleted } from 'src/easyclaim.app.screens/ReviewGrouped.shared';
import { Image, ImageSelectionService, SelectState } from 'src/easyclaim.app.services';

import { AppState } from '../AppState';
import AppStateView from '../AppStateView';
import {
	EasyClaimAppState,
	EasyClaimDocumentType,
	ProductType,
	ValidationResult,
} from '../easyclaim.api.client.typescript/src/index';
import {
	IconCarIdentification,
	IconEdit,
	IconFeedback,
	IconPartSelection,
	IconPhotoUpload,
	IconUpload,
} from '../easyclaim.app.assets';
import {
	DamageDescriptionIcon,
	DamageImagesIcon,
	FourAdditionalPicturesIcon,
	FourDiagonalPerspectivesIcon,
	ReviewIcon,
} from '../easyclaim.app.components/common/ImageRepository';

const lazyNamedScreen = (factory: () => Promise<Record<string, unknown>>, exportName: string) =>
	lazy(() =>
		factory().then((module) => ({
			default: module[exportName] as ComponentType<Record<string, unknown>>,
		}))
	);

const AdditionalDocuments = lazyNamedScreen(
	() => import('../easyclaim.app.screens/AdditionalDocuments'),
	'AdditionalDocuments'
);
const Callback = lazyNamedScreen(() => import('../easyclaim.app.screens/Callback'), 'Callback');
const DashboardMileage = lazyNamedScreen(
	() => import('../easyclaim.app.screens/DashboardMileage'),
	'DashboardMileage'
);
const Feedback = lazyNamedScreen(() => import('../easyclaim.app.screens/Feedback'), 'Feedback');
const Finished = lazyNamedScreen(() => import('../easyclaim.app.screens/Finished'), 'Finished');
const Payout = lazyNamedScreen(() => import('../easyclaim.app.screens/Payout'), 'Payout');
const Review = lazyNamedScreen(() => import('../easyclaim.app.screens/Review'), 'Review');
const SimpleApproval = lazyNamedScreen(
	() => import('../easyclaim.app.screens/SimpleApproval'),
	'SimpleApproval'
);
const Upload = lazyNamedScreen(() => import('../easyclaim.app.screens/Upload'), 'Upload');
const VehicleImages = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleImages'),
	'VehicleImages'
);
const VehicleInformation = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleInformation'),
	'VehicleInformation'
);
const VinPicture = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VinPicture'),
	'VinPicture'
);
const WaitForAudit = lazyNamedScreen(
	() => import('../easyclaim.app.screens/WaitForAudit'),
	'WaitForAudit'
);
const Approval = lazyNamedScreen(() => import('../easyclaim.app.screens/Approval'), 'Approval');
const NecessaryDocuments = lazyNamedScreen(
	() => import('../easyclaim.app.screens/NecessaryDocuments'),
	'NecessaryDocuments'
);
const Timeout = lazyNamedScreen(() => import('../easyclaim.app.screens/Timeout'), 'Timeout');
const PolicyCheckVehicleImages = lazyNamedScreen(
	() => import('../easyclaim.app.screens/PolicyCheckVehicleImages'),
	'PolicyCheckVehicleImages'
);
const VehicleFrontPerspectiveImageScreen = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleFrontPerspectiveImage'),
	'VehicleFrontPerspectiveImageScreen'
);
const VehicleRightPerspectiveImageScreen = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleRightPerspectiveImage'),
	'VehicleRightPerspectiveImageScreen'
);
const VehicleRearPerspectiveImageScreen = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleRearPerspectiveImage'),
	'VehicleRearPerspectiveImageScreen'
);
const VehicleLeftPerspectiveImageScreen = lazyNamedScreen(
	() => import('../easyclaim.app.screens/VehicleLeftPerspectiveImage'),
	'VehicleLeftPerspectiveImageScreen'
);
const FourDiagonalPerspectives = lazyNamedScreen(
	() => import('../easyclaim.app.screens/FourDiagonalPerspectives'),
	'FourDiagonalPerspectives'
);
const FourAdditionalPictures = lazyNamedScreen(
	() => import('../easyclaim.app.screens/FourAdditionalPictures'),
	'FourAdditionalPictures'
);
const OverallDashboardImage = lazyNamedScreen(
	() => import('../easyclaim.app.screens/OverallDashboardImage'),
	'OverallDashboardImage'
);
const WindshieldImage = lazyNamedScreen(
	() => import('../easyclaim.app.screens/WindshieldImage'),
	'WindshieldImage'
);
const TooManyAttempts = lazyNamedScreen(
	() => import('../easyclaim.app.screens/TooManyAttempts'),
	'TooManyAttempts'
);
const ImpactDirections = lazyNamedScreen(
	() => import('../easyclaim.app.screens/ImpactDirections'),
	'ImpactDirections'
);
const FourAdditionalDocuments = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/FourAdditionalDocuments'),
	'FourAdditionalDocuments'
);
const VehicleRegistrationDocument = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/VehicleRegistrationDocument'),
	'VehicleRegistrationDocument'
);
const LandingApproval = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/LandingApproval'),
	'LandingApproval'
);
const NoOffer = lazyNamedScreen(() => import('src/easyclaim.app.screens/NoOffer'), 'NoOffer');
const ThankYou = lazyNamedScreen(() => import('src/easyclaim.app.screens/ThankYou'), 'ThankYou');
const CashSettlementOffer = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/CashSettlementOffer'),
	'CashSettlementOffer'
);
const OfferAccepted = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/OfferAccepted'),
	'OfferAccepted'
);
const OfferRejected = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/OfferRejected'),
	'OfferRejected'
);
const RedirectEmptyScreen = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/RedirectEmptyScreen'),
	'RedirectEmptyScreen'
);
const VehicleFrontLeftPerspectiveImage = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/VehicleFrontLeftPerspectiveImage'),
	'VehicleFrontLeftPerspectiveImage'
);
const VehicleRearLeftPerspectiveImage = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/VehicleRearLeftPerspectiveImage'),
	'VehicleRearLeftPerspectiveImage'
);
const VehicleRearRightPerspectiveImage = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/VehicleRearRightPerspectiveImage'),
	'VehicleRearRightPerspectiveImage'
);
const VehicleFrontRightPerspectiveImage = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/VehicleFrontRightPerspectiveImage'),
	'VehicleFrontRightPerspectiveImage'
);
const WindshieldImageV2 = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/WIndshieldImageV2'),
	'WindshieldImageV2'
);
const VinImageV2 = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/VinImageV2'),
	'VinImageV2'
);
const DashboardImageV2 = lazyNamedScreen(
	() => import('src/easyclaim.app.screens/SInglePerspectives/DashboardImageV2'),
	'DashboardImageV2'
);
const Questions = lazyNamedScreen(() => import('src/easyclaim.app.screens/Questions'), 'Questions');
const Landing = lazy(() => import('../easyclaim.app.screens/Landing'));
const DamageImages = lazy(() => import('../easyclaim.app.screens/DamageImages'));
const DamageDescription = lazy(() => import('../easyclaim.app.screens/DamageDescription'));
const BankDetails = lazy(() => import('src/easyclaim.app.screens/BankDetails'));
const OnboardingStart = lazy(() => import('src/easyclaim.app.screens/OnboardingStart'));
const Onboarding = lazy(() => import('src/easyclaim.app.screens/Onboarding'));
const DamageImagesV2 = lazy(() => import('src/easyclaim.app.screens/DamageImagesV2'));
const ReviewGrouped = lazy(() => import('src/easyclaim.app.screens/ReviewGrouped'));

export interface getAppStateViewParams {
	isBackButtonVisibleDelegate: () => boolean;
}

export function getAppStateViews(params: getAppStateViewParams): AppStateView[] {
	async function submitMileageToApi(appState: AppState, screen: EasyClaimAppState): Promise<void> {
		const caseId = appState.easyClaimCase?.caseId;
		const mileage = appState.easyClaimCase?.vehicle?.userProvidedMileage;

		if (!caseId || mileage === null || mileage === undefined || !appState.isMileageDirty) return;
		await appState.easyClaimService?.updateScreenControl(
			caseId,
			screen,
			'dashboardMileageTextInput',
			mileage.toString()
		);
		await appState.easyClaimService?.invalidateScreenControlCache(
			caseId,
			screen,
			'dashboardMileageTextInput'
		);
	}

	const appStateViews: AppStateView[] = [
		{
			appState: EasyClaimAppState.Landing,
			isAdditionalHeaderInfoActive: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.landingConfiguration
					?.isAdditionalHeaderContentVisible ?? false,
			appStateComponentRender: (appState: AppState) => (
				<Landing
					{...appState}
					enableMotorbikesSupport={
						appState.easyClaimCase?._configuration?.enableMotorbikesSupport ?? false
					}
					acceptPrivacyDataOnLinkClick={
						appState.easyClaimCase?._configuration?.landingConfiguration
							?.isAcceptPrivacyDataOnLinkClickEnabled ?? undefined
					}
				/>
			),
		},
		{
			appState: EasyClaimAppState.OnboardingStart,
			blockAutoMoveNext: true,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.start',
			canMoveNext: () => true,
			appStateComponentRender: (appState: AppState) => <OnboardingStart {...appState} />,
		},
		{
			appState: EasyClaimAppState.Onboarding,
			blockAutoMoveNext: true,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.next',
			canMoveNext: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.landingConfiguration
					?.isTermsAndConditionsChecked === true || appState.isTermsAccepted === true,
			nextButtonDisabled: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.landingConfiguration
					?.isTermsAndConditionsChecked !== true && appState.isTermsAccepted !== true,
			appStateComponentRender: (appState: AppState) => (
				<Onboarding
					{...appState}
					onTermsAcceptedChange={(accepted) => {
						void appState.setAppState({ ...appState, isTermsAccepted: accepted });
					}}
				/>
			),
		},
		{
			appState: EasyClaimAppState.VehicleInformation,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleInformationSkipped = false)}{' '}
					<VehicleInformation
						{...appState}
						canAddLaterModal={
							appState.easyClaimCase!._configuration?.allowSkipVehicleInformation === true
						}
					/>
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleInformation',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleInformation',
			isNextButtonVisible: true,
			canMoveNext: (appState: AppState) =>
				appState.isVehicleInformationSkipped || appState.appService.isVehicleInformationCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyRegistrationDocumentWarningsOrErrors(),
			isStepsActive: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
					'allianzpt' ||
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
					'mapfrees'
					? false
					: true,
			allowClickOnSteps: (appState: AppState) =>
				!CompanyCheck.isUniqaPL(
					appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim()
				),
			iconRenderer: (appState: AppState, height: string, color: string) =>
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
				'allianzpt' ? undefined : (
					<IconCarIdentification height={height} color={color} />
				),
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canSkip: (appState: AppState) =>
				!!appState.easyClaimCase!._configuration?.allowSkipVehicleInformation === true,
			onSkip: (appState: AppState) => (appState.isVehicleInformationSkipped = true),
			showCompleteModal: async (appState: AppState) => {
				const { registrationDocument, registrationDocument2 } = appState;
				const isRegistrationDocumentUploaded =
					!!registrationDocument && registrationDocument.selectState === SelectState.Uploaded;
				const isRegistrationDocument2Uploaded =
					!!registrationDocument2 && registrationDocument2.selectState === SelectState.Uploaded;
				let items: CompleteModalItem[] = !isRegistrationDocumentUploaded
					? [{ id: 'completeVehicleInformationModal.isRegistrationDocumentUploaded' }]
					: [];

				items = !isRegistrationDocument2Uploaded
					? items.concat([
							{ id: 'completeVehicleInformationModal.isRegistrationDocument2Uploaded' },
						])
					: items;
				const canAddLater =
					appState.easyClaimCase!._configuration?.allowSkipVehicleInformation === true;

				await appState.showCompleteModal(
					items,
					canAddLater,
					'completeVehicleInformationModal.headline',
					'completeVehicleInformationModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { registrationDocument, registrationDocument2 } = appState;
				const images = [
					...(registrationDocument ? [registrationDocument] : []),
					...(registrationDocument2 ? [registrationDocument2] : []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.VehicleImages,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)} <VehicleImages {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.vehicleImages',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isVehicleImagesSkipped || appState.appService.isVehicleImagesCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyVehicleImagesWarningsOrErrors(),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageFrontLeft = appState.vehicleImageFrontLeft;
				const hasVehicleImageRearRight = appState.vehicleImageRearRight;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageFrontLeft
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageFrontLeft' }])
					: items;
				items = !hasVehicleImageRearRight
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRearRight' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageFrontLeft, vehicleImageRearRight } = appState;
				const images = [
					...(vehicleImageFrontLeft ? [vehicleImageFrontLeft] : []),
					...(vehicleImageRearRight ? [vehicleImageRearRight] : []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.PolicyCheckVehicleImages,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)} <PolicyCheckVehicleImages {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.vehicleImages',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isVehicleImagesSkipped ||
				appState.appService.isPolicyCheckVehicleImagesCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyPolicyCheckVehicleImagesWarningsOrErrors(),
			isStepsActive: (_appState: AppState) => false,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageFront = !!appState.vehicleImageFront;
				const hasVehicleImageRear = !!appState.vehicleImageRear;
				const hasVehicleImageLeft = !!appState.vehicleImageLeft;
				const hasVehicleImageRight = !!appState.vehicleImageRight;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageFront
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageFront' }])
					: items;
				items = !hasVehicleImageRear
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRear' }])
					: items;
				items = !hasVehicleImageLeft
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageLeft' }])
					: items;
				items = !hasVehicleImageRight
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRight' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageFront, vehicleImageRear, vehicleImageLeft, vehicleImageRight } =
					appState;
				const images = [
					...(vehicleImageFront ? [vehicleImageFront] : []),
					...(vehicleImageRear ? [vehicleImageRear] : []),
					...(vehicleImageRight ? [vehicleImageRight] : []),
					...(vehicleImageLeft ? [vehicleImageLeft] : []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.FourDiagonalPerspectives,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}
					<FourDiagonalPerspectives
						{...appState}
						screenConfiguration={appState.appService.getControlsConfigurations(
							EasyClaimAppState.FourDiagonalPerspectives
						)}
						enableMotorbikesSupport={
							appState.easyClaimCase?._configuration?.enableMotorbikesSupport ?? false
						}
					/>
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.fourDiagonalPerspectives',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.fourDiagonalPerspectives',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isVehicleImagesSkipped ||
				appState.appService.isFourDiagonalPerspectivesCompleted(
					EasyClaimAppState.FourDiagonalPerspectives
				),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyFourDiagonalPerspectivesWarningsOrErrors(),
			isStepsActive: (appState: AppState) => {
				const companyName = appState.easyClaimCase?._configuration?.companyShortName
					?.toLocaleLowerCase()
					.trim();
				const isUniqaPL = CompanyCheck.isUniqaPL(companyName);
				const isAllianzPtEasyClaim =
					companyName === 'allianzpt' &&
					appState.easyClaimCase?._configuration?.layout === ProductType.EasyClaim;

				const isActive = isUniqaPL || isAllianzPtEasyClaim;

				return isActive;
			},
			allowClickOnSteps: (appState: AppState) =>
				!CompanyCheck.isUniqaPL(
					appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim()
				),
			iconRenderer: (appState: AppState, height: string, color: string) => (
				<FourDiagonalPerspectivesIcon color={color} height={height} />
			),
			showCompleteModal: async (appState: AppState) => {
				const screen = EasyClaimAppState.FourDiagonalPerspectives;

				const {
					vehicleImageFrontLeft,
					vehicleImageRearLeft,
					vehicleImageFrontRight,
					vehicleImageRearRight,
				} = appState;

				const isVehicleImageFrontLeftRequired =
					appState.appService.isVehicleImageFrontLeftRequired(screen);
				const isVehicleImageRearLeftRequired =
					appState.appService.isVehicleImageRearLeftRequired(screen);
				const isVehicleImageRearRightRequired =
					appState.appService.isVehicleImageRearRightRequired(screen);
				const isVehicleImageFrontRightRequired =
					appState.appService.isVehicleImageFrontRightRequired(screen);

				const hasVehicleImageFrontLeft =
					!isVehicleImageFrontLeftRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleImageFrontLeft);
				const hasVehicleImageRearLeft =
					!isVehicleImageRearLeftRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleImageRearLeft);
				const hasVehicleImageRearRight =
					!isVehicleImageRearRightRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleImageRearRight);
				const hasVehicleImageFrontRight =
					!isVehicleImageFrontRightRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleImageFrontRight);

				let items: CompleteModalItem[] = !hasVehicleImageFrontLeft
					? [{ id: 'completeVehicleImagesModal.hasVehicleImageFrontLeft' }]
					: [];

				items = !hasVehicleImageRearLeft
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRearLeft' }])
					: items;
				items = !hasVehicleImageRearRight
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRearRight' }])
					: items;
				items = !hasVehicleImageFrontRight
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageFrontRight' }])
					: items;

				await appState.showCompleteModal(
					items,
					false,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const {
					vehicleImageFrontLeft,
					vehicleImageRearLeft,
					vehicleImageFrontRight,
					vehicleImageRearRight,
				} = appState;
				const images = [
					...(vehicleImageFrontLeft ? [vehicleImageFrontLeft] : []),
					...(vehicleImageRearLeft ? [vehicleImageRearLeft] : []),
					...(vehicleImageRearRight ? [vehicleImageRearRight] : []),
					...(vehicleImageFrontRight ? [vehicleImageFrontRight] : []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.FourAdditionalPictures,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isAdditionalDocumentsSkipped = false)}
					<FourAdditionalPictures
						{...appState}
						screenConfiguration={appState.appService.getControlsConfigurations(
							EasyClaimAppState.FourAdditionalPictures
						)}
						odometerControlInputType="Default"
						enableMotorbikesSupport={
							appState.easyClaimCase?._configuration?.enableMotorbikesSupport ?? false
						}
					/>
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.fourAdditionalPictures',
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.additionalImages',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.fourAdditionalPictures',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canSkip: (_appState: AppState) => false,
			canMoveNext: (appState: AppState) =>
				appState.isAdditionalDocumentsSkipped ||
				appState.appService.isScreenFourAdditionalPicturesCompleted(
					EasyClaimAppState.FourAdditionalPictures
				),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyFourAdditionalPicturesWarningsOrErrors(),
			isStepsActive: (appState: AppState) => {
				const companyName = appState.easyClaimCase?._configuration?.companyShortName
					?.toLocaleLowerCase()
					.trim();
				const isUniqaPL = CompanyCheck.isUniqaPL(companyName);
				const isControlExpertCEE = CompanyCheck.isControlExpertCEE(companyName);

				const isActive = isUniqaPL || isControlExpertCEE;

				return isActive;
			},
			allowClickOnSteps: (appState: AppState) =>
				!CompanyCheck.isUniqaPL(
					appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim()
				) ||
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
					'controlexpertcee',
			iconRenderer: (appState: AppState, height: string, color: string) => {
				const config = appState.easyClaimCase?._configuration;

				if (config?.companyShortName?.trim().toLowerCase() === 'mapfrees') return undefined;

				return <FourAdditionalPicturesIcon color={color} height={height} />;
			},
			showCompleteModal: async (appState: AppState) => {
				const screen = EasyClaimAppState.FourAdditionalPictures;
				const { overallDashboardImage, dashboardImage, windshieldImage, vinImage } = appState;
				const isOverallDashboardRequired =
					appState.appService.isOverallDashboardImageRequired(screen);
				const isDashboardImageRequired = appState.appService.isDashboardImageRequired(screen);
				const isWindshieldImageRequired = appState.appService.isWindshieldImageRequired(screen);
				const isVinImageRequired = appState.appService.isVinImageRequired(screen);
				const hasOverallDashboardImage =
					!isOverallDashboardRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(overallDashboardImage);
				const hasDashboardImage =
					!isDashboardImageRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(dashboardImage);
				const isDashboardMileageTextInputRequired =
					appState.appService.isDashboardMileageTextInputRequired(screen);
				const mileage = appState.easyClaimCase?.vehicle?.userProvidedMileage;
				const hasDashboardMileageTextInput = !isDashboardMileageTextInputRequired || !!mileage;
				const hasWindshieldImage =
					!isWindshieldImageRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(windshieldImage);
				const hasVINImage =
					!isVinImageRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vinImage);

				let items: CompleteModalItem[] = !hasOverallDashboardImage
					? [{ id: 'completeFourAdditionalPicturesModal.hasOverallDashboardImage' }]
					: [];

				items = !hasDashboardImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasMileageImage' }])
					: items;
				items = !hasDashboardMileageTextInput
					? items.concat([{ id: 'completeDashboardMileageModal.hasValidMileage' }])
					: items;
				items = !hasWindshieldImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasWindshieldImage' }])
					: items;
				items = !hasVINImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasVINImage' }])
					: items;

				await appState.showCompleteModal(
					items,
					false,
					'completeFourAdditionalPicturesModal.headline',
					'completeFourAdditionalPicturesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { overallDashboardImage, dashboardImage, windshieldImage, vinImage } = appState;
				const images = [
					...(overallDashboardImage ? [overallDashboardImage] : []),
					...(dashboardImage ? [dashboardImage] : []),
					...(windshieldImage ? [windshieldImage] : []),
					...(vinImage ? [vinImage] : []),
				];

				await appState.showMismatchModal(images);
			},
			onBeforeNext: async (appState: AppState) => {
				await submitMileageToApi(appState, EasyClaimAppState.FourAdditionalPictures);
			},
		},
		{
			appState: EasyClaimAppState.FourAdditionalPicturesAZCEE,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isAdditionalDocumentsSkipped = false)}
					<FourAdditionalPictures
						{...appState}
						screenConfiguration={appState.appService.getControlsConfigurations(
							EasyClaimAppState.FourAdditionalPicturesAZCEE
						)}
						odometerControlInputType="FloatingPlaceholder"
						enableMotorbikesSupport={
							appState.easyClaimCase?._configuration?.enableMotorbikesSupport ?? false
						}
					/>
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.fourAdditionalPictures',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.fourAdditionalPictures',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canSkip: (_appState: AppState) => false,
			canMoveNext: (appState: AppState) =>
				appState.isAdditionalDocumentsSkipped ||
				appState.appService.isScreenFourAdditionalPicturesCompleted(
					EasyClaimAppState.FourAdditionalPicturesAZCEE
				),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyFourAdditionalPicturesWarningsOrErrors() ||
				appState.appService.hasMileageInconsistency(appState.screen),
			isStepsActive: (_appState: AppState) => false,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const screen = EasyClaimAppState.FourAdditionalPictures;
				const { overallDashboardImage, dashboardImage, windshieldImage, vinImage } = appState;
				const isOverallDashboardRequired =
					appState.appService.isOverallDashboardImageRequired(screen);
				const isDashboardImageRequired = appState.appService.isDashboardImageRequired(screen);
				const isWindshieldImageRequired = appState.appService.isWindshieldImageRequired(screen);
				const isVinImageRequired = appState.appService.isVinImageRequired(screen);
				const hasOverallDashboardImage =
					!isOverallDashboardRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(overallDashboardImage);
				const hasDashboardImage =
					!isDashboardImageRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(dashboardImage) &&
						!appState.appService.isDashboardImageFlaggedAsRetake());
				const isDashboardMileageTextInputRequired =
					appState.appService.isDashboardMileageTextInputRequired(screen);
				const mileage = appState.easyClaimCase?.vehicle?.userProvidedMileage;
				const hasDashboardMileageTextInput = !isDashboardMileageTextInputRequired || !!mileage;
				const hasWindshieldImage =
					!isWindshieldImageRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(windshieldImage);
				const hasVINImage =
					!isVinImageRequired ||
					ImageSelectionService.hasImageSuccessfullySelectionFinished(vinImage);

				let items: CompleteModalItem[] = !hasOverallDashboardImage
					? [{ id: 'completeFourAdditionalPicturesModal.hasOverallDashboardImage' }]
					: [];

				items = !hasDashboardImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasMileageImage' }])
					: items;
				items = !hasDashboardMileageTextInput
					? items.concat([{ id: 'completeDashboardMileageModal.hasValidMileage' }])
					: items;
				items = !hasWindshieldImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasWindshieldImage' }])
					: items;
				items = !hasVINImage
					? items.concat([{ id: 'completeFourAdditionalPicturesModal.hasVINImage' }])
					: items;

				await appState.showCompleteModal(
					items,
					false,
					'completeFourAdditionalPicturesModal.headline',
					'completeFourAdditionalPicturesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				if (appState.appService.hasMileageInconsistency(appState.screen)) {
					if (!appState.isMileageMismatchModalConfirmValue) {
						await appState.setAppState({
							...appState,
							isMileageMismatchModalOpen: true,
						});

						return;
					}
				}

				let images: Image[];

				if (appState.appService.isDashboardImageFlaggedAsManualInput()) {
					const { overallDashboardImage, windshieldImage, vinImage } = appState;

					images = [
						...(overallDashboardImage ? [overallDashboardImage] : []),
						...(windshieldImage ? [windshieldImage] : []),
						...(vinImage ? [vinImage] : []),
					];
				} else {
					const { overallDashboardImage, dashboardImage, windshieldImage, vinImage } = appState;

					images = [
						...(overallDashboardImage ? [overallDashboardImage] : []),
						...(dashboardImage ? [dashboardImage] : []),
						...(windshieldImage ? [windshieldImage] : []),
						...(vinImage ? [vinImage] : []),
					];
				}
				await appState.showMismatchModal(images);
			},
			onBeforeNext: async (appState: AppState) => {
				await submitMileageToApi(appState, EasyClaimAppState.FourAdditionalPicturesAZCEE);
			},
		},
		{
			appState: EasyClaimAppState.VehicleFrontPerspectiveImage,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleFrontPerspectiveImageScreen {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleFrontPerspectiveImage',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleFrontPerspectiveImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageFront]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageFront = !!appState.vehicleImageFront;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageFront
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageFront' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageFront } = appState;
				const images = [...(vehicleImageFront ? [vehicleImageFront] : [])];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.VehicleRightPerspectiveImage,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleRightPerspectiveImageScreen {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleRightPerspectiveImage',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleRightPerspectiveImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageRight]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageRight = !!appState.vehicleImageRight;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageRight
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRight' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageRight } = appState;
				const images = [...(vehicleImageRight ? [vehicleImageRight] : [])];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.VehicleRearPerspectiveImage,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleRearPerspectiveImageScreen {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleRearPerspectiveImage',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleRearPerspectiveImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageRear]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageRear = !!appState.vehicleImageRear;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageRear
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageRear' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageRear } = appState;
				const images = [...(vehicleImageRear ? [vehicleImageRear] : [])];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.VehicleLeftPerspectiveImage,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleLeftPerspectiveImageScreen {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleLeftPerspectiveImage',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleLeftPerspectiveImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageLeft]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (appState: AppState, height: string, color: string) => (
				<IconPhotoUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const hasVehicleImageLeft = !!appState.vehicleImageLeft;
				let items: CompleteModalItem[] = [];

				items = !hasVehicleImageLeft
					? items.concat([{ id: 'completeVehicleImagesModal.hasVehicleImageLeft' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeVehicleImagesModal.headline',
					'completeVehicleImagesModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vehicleImageLeft } = appState;
				const images = [...(vehicleImageLeft ? [vehicleImageLeft] : [])];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.DashboardMileage,
			blockAutoMoveNext: true,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isDashboardMileageSkipped = false)} <DashboardMileage {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.mileage',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isDashboardMileageSkipped ||
				appState.appService.isScreenDashboardMileageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				shouldUseSolvdStyles(appState) &&
				!(
					appState.isDashboardMileageSkipped ||
					appState.appService.isScreenDashboardMileageCompleted()
				),
			hasAnyWarnings: (appState: AppState) =>
				(!appState.appService.isDashboardImageFlaggedAsManualInput() &&
					appState.appService.hasAnyWarningsOrErrors([appState.dashboardImage])) ||
				(!appState.appService.isDashboardImageFlaggedAsRetake() &&
					!appState.appService.isDashboardImageFlaggedAsManualInput() &&
					appState.appService.hasMileageInconsistency(appState.screen)),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPartSelection height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const screen = EasyClaimAppState.DashboardMileage;
				const isDashboardImageRequired = appState.appService.isDashboardImageRequired(screen);
				const isDashboardMileageTextInputRequired =
					appState.appService.isDashboardMileageTextInputRequired(screen);
				const hasDashboardImage =
					!isDashboardImageRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(appState.dashboardImage) &&
						!appState.appService.isDashboardImageFlaggedAsRetake());
				const hasDashboardMileageTextInput =
					!isDashboardMileageTextInputRequired ||
					!!appState.easyClaimCase?.vehicle?.userProvidedMileage;
				let items: CompleteModalItem[] = !hasDashboardMileageTextInput
					? [{ id: 'completeDashboardMileageModal.hasValidMileage' }]
					: [];

				items = !hasDashboardImage
					? items.concat([{ id: 'completeDashboardMileageModal.dashboardImage' }])
					: items;
				await appState.showCompleteModal(
					items,
					true,
					'completeDashboardMileageModal.headline',
					'completeDashboardMileageModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				if (!appState.isMileageMismatchModalConfirmValue) {
					await appState.setAppState({
						...appState,
						isMileageMismatchModalOpen: true,
					});

					return;
				}

				const { dashboardImage } = appState;
				const images =
					!appState.appService.isDashboardImageFlaggedAsManualInput() && dashboardImage
						? [dashboardImage]
						: [];

				await appState.showMismatchModal(images);
			},
			onBeforeNext: async (appState: AppState) => {
				await submitMileageToApi(appState, EasyClaimAppState.DashboardMileage);
			},
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.additionalImages',
		},
		{
			appState: EasyClaimAppState.OverallDashboardImage,
			appStateComponentRender: (appState: AppState) =>
				shouldUseSolvdStyles(appState) ? (
					<DashboardImageV2 {...appState} />
				) : (
					<>
						<OverallDashboardImage {...appState} />
					</>
				),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.overallDashboard',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) => appState.appService.isOverallDashboardImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				shouldUseSolvdStyles(appState) && !appState.appService.isOverallDashboardImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.overallDashboardImage]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPartSelection height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const { overallDashboardImage } = appState;
				const items: CompleteModalItem[] = !overallDashboardImage
					? [{ id: 'completeOverallDashboardImageModal.hasOverallDashboardImage' }]
					: [];

				await appState.showCompleteModal(
					items,
					true,
					'completeOverallDashboardImageModal.headline',
					'completeOverallDashboardImageModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { overallDashboardImage } = appState;
				const images = [...(overallDashboardImage ? [overallDashboardImage] : [])];

				await appState.showMismatchModal(images);
			},
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.additionalImages',
		},
		{
			appState: EasyClaimAppState.WindshieldImage,
			appStateComponentRender: (appState: AppState) =>
				shouldUseSolvdStyles(appState) ? (
					<WindshieldImageV2 {...appState} />
				) : (
					<>
						<WindshieldImage {...appState} />
					</>
				),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.windshield',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) => appState.appService.isWindshieldImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				shouldUseSolvdStyles(appState) && !appState.appService.isWindshieldImageCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.windshieldImage]),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconPartSelection height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState) => {
				const { windshieldImage } = appState;
				const items: CompleteModalItem[] = !windshieldImage
					? [{ id: 'completeWindshieldImageModal.hasWindshieldImage' }]
					: [];

				await appState.showCompleteModal(
					items,
					true,
					'completeWindshieldImageModal.headline',
					'completeWindshieldImageModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { windshieldImage } = appState;
				const images = [...(windshieldImage ? [windshieldImage] : [])];

				await appState.showMismatchModal(images);
			},
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.additionalImages',
		},
		{
			appState: EasyClaimAppState.VinPicture,
			appStateComponentRender: (appState: AppState) =>
				shouldUseSolvdStyles(appState) ? (
					<VinImageV2 {...appState} />
				) : (
					<>
						{(appState.isVinPictureSkipped = false)}
						<VinPicture {...appState} />
					</>
				),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.vinPicture',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isVinPictureSkipped || appState.appService.isVinPictureCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				shouldUseSolvdStyles(appState) &&
				!(appState.isVinPictureSkipped || appState.appService.isVinPictureCompleted()),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyVinPictureWarningsOrErrors(),
			isStepsActive: (_appState: AppState) => true,
			showCompleteModal: async (appState: AppState) => {
				const { vinImage } = appState;
				const items: CompleteModalItem[] = !vinImage ? [{ id: 'completeVinModal.hasVin' }] : [];

				await appState.showCompleteModal(
					items,
					true,
					'completeVinModal.headline',
					'completeVinModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { vinImage } = appState;
				const images = [...(vinImage ? [vinImage] : [])];

				await appState.showMismatchModal(images);
			},
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.additionalImages',
		},
		{
			appState: EasyClaimAppState.DamageImages,
			appStateComponentRender: (appState: AppState) => {
				appState.damageImagesToggle = appState.appService.computeDamageImagesToggle(appState);

				return (
					<>
						{(appState.isDamageImagesSkipped = false)}
						{
							<DamageImages
								{...appState}
								additionalDamageImagesLimit={
									appState.easyClaimCase?._configuration?.additionalDamagePicturesLimit ?? 10
								}
							/>
						}
					</>
				);
			},
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.damageImages',
			appStateLandingDescriptionId: (_appState: AppState) => 'landing.stepDescription.damageImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) => {
				const wasDamageImagesUploadCompleted = appState.appService.isDamageImagesCompleted(
					EasyClaimAppState.DamageImages
				);

				return appState.isDamageImagesSkipped || wasDamageImagesUploadCompleted;
			},
			hasAnyWarnings: (appState: AppState) => appState.appService.hasAnyDamageWarningsOrErrors(),
			isStepsActive: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
					'allianzpt' && appState.easyClaimCase?._configuration?.layout === ProductType.EasyClaim,
			iconRenderer: (appState: AppState, height: string, color: string) => {
				const config = appState.easyClaimCase?._configuration;

				if (
					config?.layout === ProductType.PolicyCheck &&
					(config?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
						config?.companyShortName?.trim().toLowerCase() === 'pevno')
				)
					return undefined;

				return <DamageImagesIcon color={color} height={height} />;
			},
			nextButtonDisabled: () => false,
			showCompleteModal: async (appState: AppState) => {
				if (
					appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
						'allianzpt' &&
					appState.easyClaimCase?._configuration?.layout === ProductType.EasyClaim
				) {
					await appState.showCompleteModal(
						[],
						false,
						'completeDamageImages.headline',
						'completeDamageImages.description',
						null,
						null
					);
				} else {
					await appState.showCompleteModal(
						[],
						true,
						'completeDamageImages.headline',
						'completeDamageImages.description',
						null,
						null
					);
				}
			},
			showMismatchModal: async (appState: AppState) => {
				const images = [
					...(appState.closeUpDamageImage ? [appState.closeUpDamageImage] : []),
					...(appState.overallDamageImage ? [appState.overallDamageImage] : []),
					...appState.additionalDamageImages,
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.AdditionalDamagePictures,
			appStateComponentRender: (appState: AppState) => {
				const displayDammageImages =
					appState.damageImagesToggle ??
					appState.appService.computeDamageImagesToggle(appState) ??
					true;

				return (
					<>
						{(appState.isDamageImagesSkipped = false)}
						{
							<DamageImagesV2
								{...appState}
								displayDamageImages={displayDammageImages}
								additionalDamageImagesLimit={
									appState.easyClaimCase?._configuration?.additionalDamagePicturesLimit ?? 10
								}
								additionalDamagePicturesRequired={
									appState.easyClaimCase?._configuration?.additionalDamagePicturesRequired ?? false
								}
							/>
						}
					</>
				);
			},
			appStateReviewDescriptionId: () => 'review.stepDescription.damageImages',
			appStateLandingDescriptionId: () => 'landing.stepDescription.damageImages',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) => {
				const damageImagesUploadCompleted =
					appState.appService.isSolvdDamageImagesCompleted(appState);

				return appState.isDamageImagesSkipped || damageImagesUploadCompleted;
			},
			hasAnyWarnings: (appState: AppState) => appState.appService.hasAnyDamageWarningsOrErrors(),
			iconRenderer: (appState: AppState, height: string, color: string) => {
				const config = appState.easyClaimCase?._configuration;

				if (
					config?.layout === ProductType.PolicyCheck &&
					(config?.companyShortName?.trim().toLowerCase() === 'uniqapl' ||
						config?.companyShortName?.trim().toLowerCase() === 'pevno')
				)
					return undefined;

				return <DamageImagesIcon color={color} height={height} />;
			},
			nextButtonDisabled: (appState: AppState) => {
				const damageImagesUploadCompleted =
					appState.appService.isSolvdDamageImagesCompleted(appState);

				return !damageImagesUploadCompleted;
			},
			showCompleteModal: async (appState: AppState) => {
				await appState.showCompleteModal(
					[],
					true,
					'completeDamageImages.headline',
					'completeDamageImages.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const images = [
					...(appState.closeUpDamageImage ? [appState.closeUpDamageImage] : []),
					...(appState.overallDamageImage ? [appState.overallDamageImage] : []),
					...appState.additionalDamageImages,
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.DamageDescription,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isDamageDescriptionSkipped = false)}
					<DamageDescription {...appState} maximumDamageDescriptionLength={300} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.damageDescription',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.damageDescription',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isDamageDescriptionSkipped || appState.appService.isDamageDescriptionCompleted(),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<DamageDescriptionIcon color={color} height={height} />
			),
			showCompleteModal: async (appState: AppState) => {
				await appState.showCompleteModal(
					[],
					false,
					'completeDamageDescriptionModal.headline',
					'completeDamageDescriptionModal.description',
					null,
					null
				);
			},
		},
		{
			appState: EasyClaimAppState.AdditionalDocuments,
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isAdditionalDocumentsSkipped = false)}
					<AdditionalDocuments {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.additionalDocuments',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.additionalDocuments',
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			canMoveNext: (appState: AppState) =>
				appState.isAdditionalDocumentsSkipped ||
				appState.appService.IsAdditionalDocumentsCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyDocumentPictureWarningsOrErrors(),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconEdit height={height} color={color} />
			),
			canSkip: (_appState: AppState) => true,
			onSkip: (appState: AppState) => (appState.isAdditionalDocumentsSkipped = true),
			showCompleteModal: async (appState: AppState) => {
				await appState.showCompleteModal(
					[],
					false,
					'completeAdditionalDocuments.headline',
					'completeAdditionalDocuments.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState) => {
				const { additionalDocumentImage, additionalDocumentImages } = appState;
				const images = [
					...(additionalDocumentImage ? [additionalDocumentImage] : []),
					...additionalDocumentImages,
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.Timeout,
			appStateComponentRender: (appState: AppState) => <Timeout {...appState} />,
			isNextButtonVisible: false,
			isBackButtonVisible: () => false,
		},
		{
			appState: EasyClaimAppState.Review,
			appStateComponentRender: (appState: AppState) =>
				shouldUseSolvdStyles(appState) ? <ReviewGrouped {...appState} /> : <Review {...appState} />,
			blockAutoMoveNext: true,
			isNextButtonVisible: true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			nextButtonTextId: 'review.button.next',
			canMoveNext: (appState: AppState) => {
				if (
					!appState.easyClaimCase ||
					!appState.easyClaimCase._configuration ||
					!appState.easyClaimCase._configuration.frontendSteps
				) {
					return false;
				}
				const frontendSteps = appState.easyClaimCase._configuration.frontendSteps;
				const canMove = appState.appStateViews
					.filter((step) => step.appState !== EasyClaimAppState.Review)
					.filter(
						(step) =>
							frontendSteps.filter((fs) => fs.appState === step.appState).length > 0 &&
							step.canMoveNext
					)
					.map((step) => step.canMoveNext!(appState))
					.reduce((p, c) => p && c);

				return canMove;
			},
			nextButtonDisabled: (appState: AppState) =>
				shouldUseSolvdStyles(appState) && !isReviewGroupedCompleted(appState),
			isStepsActive: (_appState: AppState) => true,
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<ReviewIcon color={color} height={height} />
			),
		},
		{
			appState: EasyClaimAppState.Upload,
			appStateComponentRender: (appState: AppState) => <Upload {...appState} />,
			canMoveNext: (_appState: AppState) => true,
		},
		{
			appState: EasyClaimAppState.WaitForAudit,
			appStateComponentRender: (_appState: AppState) => <WaitForAudit />,
		},
		{
			appState: EasyClaimAppState.SimpleApproval,
			appStateComponentRender: (appState: AppState) => <SimpleApproval {...appState} />,
			isBackButtonVisible: () => true,
			isNextButtonVisible: false,
		},
		{
			appState: EasyClaimAppState.Approval,
			appStateComponentRender: (appState: AppState) => <Approval {...appState} />,
			isBackButtonVisible: () => true,
			isNextButtonVisible: false,
		},
		{
			appState: EasyClaimAppState.Payout,
			appStateComponentRender: (appState: AppState) => <Payout {...appState} />,
			canMoveNext: (appState: AppState) =>
				appState.appService.isBankAccountOwnerValid() &&
				appState.appService.isBankAccountNumberValid(),
			isNextButtonVisible: true,
			showCompleteModal: async (appState: AppState) => {
				let items: CompleteModalItem[] = [];
				const hasValidAccountOwner = appState.appService.isBankAccountOwnerValid();
				const hasIban = appState.appService.isBankAccountNumberSpecified();
				const hasValidIban = appState.appService.isBankAccountNumberValid();

				items = !hasValidAccountOwner
					? items.concat([{ id: 'completePayoutInformationModal.accountHolder' }])
					: items;
				items = !hasIban
					? items.concat([{ id: 'completePayoutInformationModal.accountHolderIBAN' }])
					: items;
				items =
					!hasIban && !hasValidIban
						? items.concat([{ id: 'completePayoutInformationModal.accountHolderIBANInvalid' }])
						: items;
				await appState.showCompleteModal(
					items,
					false,
					'completePayoutInformationModal.headline',
					'completePayoutInformationModal.description',
					null,
					null
				);
			},
		},
		{
			appState: EasyClaimAppState.Finished,
			appStateComponentRender: (appState: AppState) => (
				<Finished
					easyClaimCalculationResult={appState.easyClaimCalculationResult}
					allowDownloadCalculationFileOnFinishedView={
						appState.appService.isCalculationFileDownloadingAllowedOnFinishedView
					}
				/>
			),
		},
		{
			appState: EasyClaimAppState.Callback,
			appStateComponentRender: (_appState: AppState) => <Callback />,
		},
		{
			appState: EasyClaimAppState.Feedback,
			appStateComponentRender: (appState: AppState) => (
				<Feedback
					{...appState}
					feedbackMaxLength={
						appState.easyClaimCase?._configuration?.customerFeedbackMaxLength || 500
					}
				/>
			),
			appStateLandingDescriptionId: (_appState: AppState) => 'landing.stepDescription.feedback',
			iconRenderer: (appState: AppState, height: string, color: string) => (
				<IconFeedback height={height} color={color} />
			),
			isNextButtonVisible: true,
			canMoveNext: (_: AppState) => true,
			nextButtonTextId: 'feedback.button.send',
			blockAutoMoveNext: true,
		},
		{
			appState: EasyClaimAppState.NecessaryDocuments,
			appStateComponentRender: (appState: AppState) => <NecessaryDocuments {...appState} />,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			isNextButtonVisible: true,
			canMoveNext: (_appState: AppState) => true,
		},
		{
			appState: EasyClaimAppState.Timeout,
			appStateComponentRender: (appState: AppState) => <Timeout {...appState} />,
		},
		{
			appState: EasyClaimAppState.TooManyAttempts,
			appStateComponentRender: (appState: AppState) => <TooManyAttempts {...appState} />,
		},
		{
			appState: EasyClaimAppState.ImpactDirection,
			appStateComponentRender: (appState: AppState) => (
				<ImpactDirections {...appState} clickableImpactDirections={false} />
			),
			isNextButtonVisible: true,
			canMoveNext: (_appState: AppState) => true,
			isBackButtonVisible: () => params.isBackButtonVisibleDelegate(),
			nextButtonTextId: 'impactDirections.button.next',
		},
		{
			appState: EasyClaimAppState.FourAdditionalDocuments,
			appStateComponentRender: (appState: AppState) => (
				<>
					<FourAdditionalDocuments
						{...appState}
						screenConfiguration={appState.appService.getControlsConfigurations(
							EasyClaimAppState.FourAdditionalDocuments
						)}
					/>
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.fourAdditionalDocuments',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.fourAdditionalDocuments',
			isNextButtonVisible: true,
			isBackButtonVisible: (): boolean => params.isBackButtonVisibleDelegate(),
			canSkip: (_appState: AppState): boolean => false,
			canMoveNext: (appState: AppState) =>
				appState.appService.isScreenFourAdditionalDocumentsCompleted(
					EasyClaimAppState.FourAdditionalDocuments
				),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyFourAdditionalDocumentsWarningsOrErrors(),
			iconRenderer: (appState: AppState, height: string, color: string) =>
				appState.easyClaimCase?._configuration?.companyShortName?.toLocaleLowerCase().trim() ===
				'mapfrees' ? (
					<IconCarIdentification height={height} color={color} />
				) : (
					<IconUpload height={height} color={color} />
				),
			showCompleteModal: async (appState: AppState): Promise<void> => {
				const screen = EasyClaimAppState.FourAdditionalDocuments;
				const {
					registrationDocument,
					registrationDocument2,
					dniOutside,
					dniInside,
					driverLicenseOutside,
					driverLicenseInside,
					vehicleDatasheetOutside,
					vehicleDatasheetInside,
					itv,
				} = appState;

				const isRegistrationDocumentRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.RegistrationDocument
					);
				const isRegistrationDocumentInsideRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.RegistrationDocumentInside
					);
				const isDniOutsideRequired = appState.appService.isDocumentTypeImageControlRequired(
					screen,
					EasyClaimDocumentType.DniOutside
				);
				const isDniInsideRequired = appState.appService.isDocumentTypeImageControlRequired(
					screen,
					EasyClaimDocumentType.DniInside
				);
				const isDriverLicenseOutsideRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.DriverLicenseOutside
					);
				const isDriverLicenseInsideRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.DriverLicenseInside
					);
				const isVehicleDatasheetOutsideRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.VehicleDatasheetOutside
					);
				const isVehicleDatasheetInsideRequired =
					appState.appService.isDocumentTypeImageControlRequired(
						screen,
						EasyClaimDocumentType.VehicleDatasheetInside
					);
				const isItvRequired = appState.appService.isDocumentTypeImageControlRequired(
					screen,
					EasyClaimDocumentType.ITV
				);

				const hasRegistrationDocument =
					!isRegistrationDocumentRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(registrationDocument) &&
						registrationDocument?.selectState !== SelectState.Error);
				const hasRegistrationDocumentInside =
					!isRegistrationDocumentInsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(registrationDocument2) &&
						registrationDocument2?.selectState !== SelectState.Error);
				const hasDniOutside =
					!isDniOutsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(dniOutside) &&
						dniOutside?.selectState !== SelectState.Error);
				const hasDniInside =
					!isDniInsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(dniInside) &&
						dniInside?.selectState !== SelectState.Error);
				const hasDriverLicenseOutside =
					!isDriverLicenseOutsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(driverLicenseOutside) &&
						driverLicenseOutside?.selectState !== SelectState.Error);
				const hasDriverLicenseInside =
					!isDriverLicenseInsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(driverLicenseInside) &&
						driverLicenseInside?.selectState !== SelectState.Error);
				const hasVehicleDatasheetOutside =
					!isVehicleDatasheetOutsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleDatasheetOutside) &&
						vehicleDatasheetOutside?.selectState !== SelectState.Error);
				const hasVehicleDatasheetInside =
					!isVehicleDatasheetInsideRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(vehicleDatasheetInside) &&
						vehicleDatasheetInside?.selectState !== SelectState.Error);
				const hasItv =
					!isItvRequired ||
					(ImageSelectionService.hasImageSuccessfullySelectionFinished(itv) &&
						itv?.selectState !== SelectState.Error);

				let items: CompleteModalItem[] = !hasRegistrationDocument
					? [{ id: 'fourAdditionalDocuments.RegistrationDocument.title' }]
					: [];

				items = !hasRegistrationDocumentInside
					? items.concat([{ id: 'fourAdditionalDocuments.RegistrationDocumentInside.title' }])
					: items;
				items = !hasDniOutside
					? items.concat([{ id: 'fourAdditionalDocuments.DniOutside.title' }])
					: items;
				items = !hasDniInside
					? items.concat([{ id: 'fourAdditionalDocuments.DniInside.title' }])
					: items;
				items = !hasDriverLicenseOutside
					? items.concat([{ id: 'fourAdditionalDocuments.DriverLicenseOutside.title' }])
					: items;
				items = !hasDriverLicenseInside
					? items.concat([{ id: 'fourAdditionalDocuments.DriverLicenseInside.title' }])
					: items;
				items = !hasItv ? items.concat([{ id: 'fourAdditionalDocuments.ITV.title' }]) : items;
				items = !hasVehicleDatasheetOutside
					? items.concat([{ id: 'fourAdditionalDocuments.VehicleDatasheetOutside.title' }])
					: items;
				items = !hasVehicleDatasheetInside
					? items.concat([{ id: 'fourAdditionalDocuments.VehicleDatasheetInside.title' }])
					: items;

				await appState.showCompleteModal(
					items,
					false,
					'completeFourAdditionalDocumentsModal.headline',
					'completeFourAdditionalDocumentsModal.description',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState): Promise<void> => {
				const {
					registrationDocument,
					registrationDocument2,
					dniOutside,
					dniInside,
					driverLicenseOutside,
					driverLicenseInside,
					vehicleDatasheetOutside,
					vehicleDatasheetInside,
					itv,
				} = appState;
				const images = [
					...(registrationDocument ? [registrationDocument] : []),
					...(registrationDocument2 ? [registrationDocument2] : []),
					...(dniOutside ? [dniOutside] : []),
					...(dniInside ? [dniInside] : []),
					...(driverLicenseOutside ? [driverLicenseOutside] : []),
					...(driverLicenseInside ? [driverLicenseInside] : []),
					...(vehicleDatasheetOutside ? [vehicleDatasheetOutside] : []),
					...(vehicleDatasheetInside ? [vehicleDatasheetInside] : []),
					...(itv ? [itv] : []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.VehicleRegistrationDocument,
			blockAutoMoveNext: true,
			appStateComponentRender: (appState: AppState) => (
				<VehicleRegistrationDocument
					{...appState}
					screenConfiguration={appState.appService.getControlsConfigurations(
						EasyClaimAppState.VehicleRegistrationDocument
					)}
				/>
			),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleRegistrationDocument',
			appStateLandingDescriptionId: (_appState: AppState) =>
				'landing.stepDescription.vehicleRegistrationDocument',
			isNextButtonVisible: true,
			isBackButtonVisible: (): boolean => params.isBackButtonVisibleDelegate(),
			isStepsActive: (_appState: AppState) => true,
			canSkip: (_appState: AppState): boolean => false,
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleRegistrationDocumentCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				!appState.appService.isVehicleRegistrationDocumentCompleted(),
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyVehicleRegistrationDocumentWarningsOrErrors(
					EasyClaimAppState.VehicleRegistrationDocument
				),
			iconRenderer: (_appState: AppState, height: string, color: string) => (
				<IconUpload height={height} color={color} />
			),
			showCompleteModal: async (appState: AppState): Promise<void> => {
				const controls = appState.appService.getControlsConfigurations(
					EasyClaimAppState.VehicleRegistrationDocument
				);
				const images = new Map<EasyClaimDocumentType, Image | undefined>([
					[EasyClaimDocumentType.RegistrationDocument, appState.registrationDocument],
					[EasyClaimDocumentType.RegistrationDocumentInside, appState.registrationDocument2],
				]);
				const items: CompleteModalItem[] = controls
					.filter(({ documentType, isRequired }) => {
						if (!documentType || !isRequired) return false;

						const image = images.get(documentType);

						return !(
							ImageSelectionService.hasImageSuccessfullySelectionFinished(image) &&
							image?.selectState !== SelectState.Error
						);
					})
					.map(({ documentType }) => ({
						id: `vehicleRegistrationDocument.${documentType}.validationRequired`,
					}));

				await appState.showCompleteModal(
					items,
					false,
					'vehicleRegistrationDocument.validationHeadline',
					'vehicleRegistrationDocument.validationDescription',
					null,
					null
				);
			},
			showMismatchModal: async (appState: AppState): Promise<void> => {
				const documentTypes = appState.appService
					.getControlsConfigurations(EasyClaimAppState.VehicleRegistrationDocument)
					.map(({ documentType }) => documentType);
				const images = [
					...(documentTypes.includes(EasyClaimDocumentType.RegistrationDocument) &&
					appState.registrationDocument
						? [appState.registrationDocument]
						: []),
					...(documentTypes.includes(EasyClaimDocumentType.RegistrationDocumentInside) &&
					appState.registrationDocument2
						? [appState.registrationDocument2]
						: []),
				];

				await appState.showMismatchModal(images);
			},
		},
		{
			appState: EasyClaimAppState.LandingApproval,
			isAdditionalHeaderInfoActive: (appState: AppState) =>
				appState.easyClaimCase?._configuration?.landingConfiguration
					?.isAdditionalHeaderContentVisible ?? false,
			appStateComponentRender: (appState: AppState) => <LandingApproval {...appState} />,
		},
		{
			appState: EasyClaimAppState.NoOffer,
			appStateComponentRender: (appState: AppState) => <NoOffer {...appState} />,
		},
		{
			appState: EasyClaimAppState.ThankYou,
			appStateComponentRender: (appState: AppState) => <ThankYou {...appState} />,
			isNextButtonVisible: true,
			canMoveNext: (_: AppState) => true,
			nextButtonTextId: 'thankYou.button.next',
		},
		{
			appState: EasyClaimAppState.BankDetails,
			appStateComponentRender: (appState: AppState) => <BankDetails {...appState} />,
			canMoveNext: (appState: AppState) => appState.appService.isScreenBankDetailsCompleted(),
			showCompleteModal: async (appState: AppState) => {
				const bankDetailsStatement = appState.bankAccountStatement;
				const iban = appState.easyClaimCase?.bankAccountNumber?.replaceAll(/\s/g, '');
				const isCorrectCountry = iban?.substring(0, 2) === 'PT' && iban.length === 25;
				const uploadErrors = bankDetailsStatement?.uploadResult?.validationResults?.filter(
					(x) => x.validationResult === ValidationResult.Error
				);
				let items: CompleteModalItem[] = [];

				items = uploadErrors
					? items.concat([{ id: uploadErrors[0].description ?? 'bankDetails.modal.wrongFile' }])
					: items;
				items =
					!appState.appService.isBankAccountNumberValid() && isCorrectCountry
						? items.concat([{ id: 'bankDetails.modal.invalidIban' }])
						: items;
				items = !isCorrectCountry
					? items.concat([{ id: 'bankDetails.modal.notPortugueseError' }])
					: items;
				items = !iban ? items.concat([{ id: 'bankDetails.iban.emptyIban' }]) : items;
				items =
					appState.appService.isBankStatementDocumentRequired() &&
					!bankDetailsStatement &&
					items.length === 0
						? items.concat([{ id: 'bankDetails.modal.notUploaded' }])
						: items;
				await appState.showCompleteModal(
					items,
					false,
					'bankDetails.modal.headline',
					'bankDetails.modal.description',
					null,
					null
				);
			},
		},
		{
			appState: EasyClaimAppState.CashSettlementOffer,
			appStateComponentRender: (appState: AppState) => <CashSettlementOffer {...appState} />,
		},
		{
			appState: EasyClaimAppState.OfferAccepted,
			appStateComponentRender: (appState: AppState) => (
				<OfferAccepted
					{...appState}
					feedbackMaxLength={
						appState.easyClaimCase?._configuration?.customerFeedbackMaxLength || 500
					}
				/>
			),
			isNextButtonVisible: true,
			canMoveNext: (_: AppState) => true,
			nextButtonTextId: 'feedback.button.send',
		},
		{
			appState: EasyClaimAppState.OfferRejected,
			appStateComponentRender: (appState: AppState) => (
				<OfferRejected
					{...appState}
					feedbackMaxLength={
						appState.easyClaimCase?._configuration?.customerFeedbackMaxLength || 500
					}
				/>
			),
			isNextButtonVisible: true,
			canMoveNext: (_: AppState) => true,
			nextButtonTextId: 'feedback.button.send',
		},
		{
			appState: EasyClaimAppState.Redirect,
			appStateComponentRender: (_appState: AppState) => <RedirectEmptyScreen />,
		},
		{
			appState: EasyClaimAppState.VehicleFrontLeftPerspectiveImage,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.continue',
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleFrontLeftPerspectiveImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				!appState.appService.isVehicleFrontLeftPerspectiveImageCompleted(),
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleFrontLeftPerspectiveImage {...appState} />
				</>
			),
			isStepsActive: (_appState: AppState) => true,
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageFrontLeft]),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleFrontLeftPerspective',
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.perspectiveImages',
		},
		{
			appState: EasyClaimAppState.VehicleRearLeftPerspectiveImage,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.continue',
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleRearLeftPerspectiveImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				!appState.appService.isVehicleRearLeftPerspectiveImageCompleted(),
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleRearLeftPerspectiveImage {...appState} />
				</>
			),
			isStepsActive: (_appState: AppState) => true,
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageRearLeft]),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleRearLeftPerspective',
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.perspectiveImages',
		},
		{
			appState: EasyClaimAppState.VehicleRearRightPerspectiveImage,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.continue',
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleRearRightPerspectiveImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				!appState.appService.isVehicleRearRightPerspectiveImageCompleted(),
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleRearRightPerspectiveImage {...appState} />
				</>
			),
			isStepsActive: (_appState: AppState) => true,
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageRearRight]),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleRearRightPerspective',
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.perspectiveImages',
		},
		{
			appState: EasyClaimAppState.VehicleFrontRightPerspectiveImage,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.continue',
			canMoveNext: (appState: AppState) =>
				appState.appService.isVehicleFrontRightPerspectiveImageCompleted(),
			nextButtonDisabled: (appState: AppState) =>
				!appState.appService.isVehicleFrontRightPerspectiveImageCompleted(),
			appStateComponentRender: (appState: AppState) => (
				<>
					{(appState.isVehicleImagesSkipped = false)}{' '}
					<VehicleFrontRightPerspectiveImage {...appState} />
				</>
			),
			isStepsActive: (_appState: AppState) => true,
			hasAnyWarnings: (appState: AppState) =>
				appState.appService.hasAnyWarningsOrErrors([appState.vehicleImageFrontRight]),
			appStateReviewDescriptionId: (_appState: AppState) =>
				'review.stepDescription.vehicleFrontRightPerspective',
			appStateReviewGroupId: (_appState: AppState) =>
				'review.stepDescription.group.perspectiveImages',
		},
		{
			appState: EasyClaimAppState.Questions,
			isNextButtonVisible: true,
			nextButtonTextId: 'button.continue',
			canMoveNext: (appState: AppState) => appState.appService.isQuestionsCompleated(),
			nextButtonDisabled: (appState: AppState) => !appState.appService.isQuestionsCompleated(),
			isStepsActive: (_appState: AppState) => true,
			appStateComponentRender: (appState: AppState) => (
				<>
					<Questions {...appState} />
				</>
			),
			appStateReviewDescriptionId: (_appState: AppState) => 'review.stepDescription.questions',
		},
	];

	return appStateViews;
}

function shouldUseSolvdStyles(appstate: AppState): boolean {
	return !!appstate.easyClaimCase?._configuration?.useSolvdStyles;
}
